import { z } from 'zod';

/**
 * Zod schema for validating a user's first name.
 * Must be non-empty, contain at least one letter, and allow letters, spaces, dots, apostrophes and hyphens.
 */
export const firstNameSchema =
  /**
   * Zod schema for validating a user's first name.
   * Must be non-empty, contain at least one letter, and allow letters, spaces, dots, apostrophes and hyphens.
   */
  z
    .string()
    .min(1, { message: 'zod.first-name-is-required' })
    .regex(/^[a-zA-Z .'-]*[a-zA-Z][a-zA-Z .'-]*$/, {
      message: 'zod.first-name-format',
    });

export const nameForDeviceSchema =
  /**
   * Zod schema for validating a device or user name. Must be non-empty.
   */
  z.string().min(1, { message: 'zod.name-is-required' });

export const deviceIdSchema =
  /**
   * Zod schema for validating a device ID. Must be non-empty.
   */
  z.string().min(1, { message: 'zod.device-id-is-required' });

export const kioskIdSchema =
  /**
   * Zod schema for validating a kiosk ID. Must be non-empty.
   */
  z.string().min(1, { message: 'zod.kiosk-id-is-required' });

export const lastNameSchema =
  /**
   * Zod schema for validating a user's last name.
   * Must be non-empty, contain at least one letter, and allow letters, spaces, dots, apostrophes and hyphens.
   */
  z
    .string()
    .min(1, { message: 'zod.last-name-is-required' })
    .regex(/^[a-zA-Z .'-]*[a-zA-Z][a-zA-Z .'-]*$/, {
      message: 'zod.last-name-format',
    });

export const emailSchemaRequired =
  /**
   * Zod schema for validating required email addresses.
   * Must be non-empty and in valid email format.
   */
  z
    .string()
    .min(1, { message: 'zod.email-is-required' })
    .email({ message: 'common.invalid-email' });

export const dobSchema =
  /**
   * Zod schema for validating date of birth.
   * Must be a valid date not in the future.
   */
  z
    .date({
      required_error: 'zod.field-required',
      invalid_type_error: 'zod.invalid-date-format',
    })
    .refine((date) => date <= new Date(), {
      message: 'zod.dob-future-error',
    })
    .refine((date) => !isNaN(date.getTime()), {
      message: 'zod.invalid-date',
    });

export const commonSchema =
  /**
   * Zod schema for validating required string fields.
   * Must be non-empty.
   */
  z.string().min(1, { message: 'zod.field-required' });

export const generalTextSchema =
  /**
   * Zod schema for validating general text fields.
   * Allows letters, numbers, spaces, and common punctuation.
   */
  z.string().regex(/^[\p{L}|\p{N}\s(.,-/?:@=&$+`'ˆ~!*_)]*$/u, {
    message: 'zod.invalid-general-text',
  });

export const phoneSchema =
  /**
   * Zod schema for validating optional phone numbers.
   * Cleans non-digit characters and enforces 4-12 digits when provided.
   */
  z
    .object({
      country: z.string().optional(),
      number: z
        .string()
        .trim()
        .optional()
        .refine((val) => !val || (val.length >= 4 && val.length <= 12), {
          message: 'zod.phone-number-must-contain-from-4-to-12-digits',
        })
        .transform((val) =>
          val === '' || !val ? undefined : val.replace(/\D/g, ''),
        )
        .refine((val) => !val || /^[0-9]{1,18}$/.test(val), {
          message: 'zod.phone-format',
        }),
    })
    .transform((data) => {
      if (!data.number) {
        return { ...data, country: undefined };
      }
      return { ...data, country: data.country ?? 'US' };
    });

export const strictPhoneSchema =
  /**
   * Zod schema for validating required phone numbers.
   * Country and number must be non-empty; number allows digits, spaces, parentheses, and hyphens.
   */
  z.object({
    country: z.string().min(1, 'zod.field-required'),
    number: z
      .string()
      .trim()
      .min(1, 'zod.field-required')
      .min(4, 'zod.phone-number-must-contain-from-4-to-12-digits')
      .max(12, 'zod.phone-number-must-contain-from-4-to-12-digits')
      .regex(/^[0-9 ()-]+$/, {
        message: 'zod.phone-format',
      }),
  });

export const nameSchema =
  /**
   * Zod schema for validating names.
   * Accepts only letters, spaces, periods, apostrophes, and hyphens.
   */
  z.string().regex(/^[a-zA-Z .'-]*$/, {
    message: 'zod.name-format',
  });
